import { useCallback, useState } from "react";
import { userIsPublic } from "~/domains/user/utils/roles";
import { getRedirectToRegisterPage, redirectToUrl } from "~/utils/url/urlUtils";
import { trackSrpAddToWishlist } from "~/app/analytics/ga/ga";
import { type BaseProductData, type ProductData } from "~/domains/product/types/ProductData";
import type { OptionalPartDraft } from "~/features/addToCart/hooks/useAddToCartForm";
import { openAddToWishlistWizard } from "~/features/wishlist/components/modals/AddToWishlist/AddToWishlistService";
import { mapOptionalParts4Wishlist } from "~/features/wishlist/components/modals/AddToWishlist/utils";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";

export interface UseAddProductToWishlistFlowParams {
  productInput?: ProductData;
  initialQuantity?: number;
  initialPriceReference?: string;
  selectedOptionalParts?: Dict<OptionalPartDraft>;
}

export const useAddProductToWishlistFlow = ({
  productInput,
  initialQuantity = 1,
  initialPriceReference = getInitialPriceReference(),
  selectedOptionalParts,
}: UseAddProductToWishlistFlowParams) => {
  // set a default product if it wasn't provided by productInput
  const [product, setProduct] = useState(productInput || { designation: "", code: "", isOptime: false });

  const onClick = useCallback(
    (productData?: BaseProductData) => {
      // set new product by click if available
      productData && setProduct(productData);

      const data = productData || product;

      if (userIsPublic()) {
        redirectToUrl(getRedirectToRegisterPage({ openLogin: true }));
      } else {
        trackSrpAddToWishlist(data.designation, 1);

        openAddToWishlistWizard({
          productCode: data.code,
          initialQuantity: initialQuantity,
          initialPriceReference: initialPriceReference,
          addTracking: (product) => trackSrpAddToWishlist(product.base.designation, 2),
          selectedOptionalParts: mapOptionalParts4Wishlist(data as ProductData, selectedOptionalParts),
        });
      }
    },
    [product.designation, initialPriceReference, initialQuantity],
  );

  const modal = false;

  return [onClick, modal] as const;
};
